import React, { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BarcodeStatus, MasterBarcodeEntry, MasterCropData, MasterUPCData, SectionTarget } from '../../types';
import { CropThumbnail } from './CropThumbnail';
import { ActionMode } from '../../hooks/useUPCActions';
import { getCachedImageUrl, ZoomMeta } from '../../hooks/useZoomPreview';
import { normalizeBarcode } from '../../utils/normalizeBarcode';
import { buildDerivedBarcodeRows, getAllBarcodeEntries, getDesignBarcodeInfo } from '../../utils/masterBarcodeRows';

// ── Types ──

export interface FocusedHighlightInfo {
  type: string;
  cropId?: string;
  cropIds?: string[];
  cropItemNumber?: string;
}

export interface UPCColumnViewProps {
  upc: MasterUPCData;
  // Selection
  selectedCropIds: Set<string>;
  onCropClick: (cropId: string, shiftKey: boolean) => void;
  onSectionClick: (target: SectionTarget) => void;
  // Mode
  mode: ActionMode;
  mergeSourceDesignId: string | null;
  activeDesignId: string | null;
  onActiveDesignChange: (id: string | null) => void;
  // Design colors
  designColorMap: Map<string, string>;
  // Highlight (optional — only used by Design Review)
  focusedHighlight?: FocusedHighlightInfo | null;
  isSectionHighlighted?: (sectionKey: string, target: SectionTarget, count?: number) => boolean;
  // Zoom
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
  // Optional: remove button for Master Label
  onRemoveUpc?: (upc: string) => void;
  // Show crop type triangles (demo/task player only)
  showTriangles?: boolean;
  // Recently moved crops (for glow animation)
  recentlyMovedCropIds?: Set<string>;
  // Custom crop thumbnail size (also drives column width)
  cropSize?: number;
  // Text scale percentage for this column UI
  textScale?: number;
  // Retry token for crop image reloads
  reloadVersion?: number;
  // Preload a group of image URLs on first hover
  onPreloadGroup?: (urls: string[]) => void;
  highlightedCropIds?: Set<string>;
  highlightColor?: string;
  highlightGlow?: string;
}

type CropBadgeConfig = {
  topLeftMode: 'number' | 'warning' | null;
  topLeftLabel?: string;
  topLeftBackground?: string;
  topLeftBorder?: string;
  topLeftColor?: string;
  sourceLabel: 'S' | 'F' | 'C' | 'O';
  typeLabel: 'P' | 'C';
};

// ── Component ──

export const UPCColumnView: React.FC<UPCColumnViewProps> = ({
  upc,
  selectedCropIds,
  onCropClick,
  onSectionClick,
  mode,
  mergeSourceDesignId,
  activeDesignId,
  onActiveDesignChange,
  designColorMap,
  focusedHighlight,
  isSectionHighlighted,
  onImageHover,
  onImageMove,
  onImageLeave,
  onRemoveUpc,
  showTriangles = false,
  recentlyMovedCropIds,
  cropSize,
  textScale = 100,
  reloadVersion = 0,
  onPreloadGroup,
  highlightedCropIds,
  highlightColor,
  highlightGlow,
}) => {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set([`cases-${upc.itemNumber}`]));
  const preloadedGroups = React.useRef(new Set<string>());
  const HEADER_SIDE_WIDTH = 22;
  const HEADER_COUNT_WIDTH = 28;
  const resolvedImageSize = cropSize ?? 100;
  const resolvedTextScale = textScale / 100;
  const scaleText = (px: number) => Math.round(px * resolvedTextScale * 100) / 100;
  const upcEntryImageSize = Math.min(resolvedImageSize, 72);
  const upcBadgeColumnWidth = 16;
  const upcCodeColumnWidth = 'fit-content(13ch)';
  const upcEntryColumnGap = 8;
  const UPC_NUMBER_COLORS = [
    { background: 'rgba(88, 166, 255, 0.9)', border: '1px solid rgba(145, 193, 255, 0.6)', color: '#f3f9ff' },
    { background: 'rgba(248, 81, 73, 0.92)', border: '1px solid rgba(255, 160, 153, 0.6)', color: '#fff5f5' },
    { background: 'rgba(163, 113, 247, 0.92)', border: '1px solid rgba(204, 177, 255, 0.6)', color: '#fbf8ff' },
    { background: 'rgba(100, 116, 139, 0.92)', border: '1px solid rgba(179, 190, 208, 0.55)', color: '#f8fafc' },
    { background: 'rgba(236, 72, 153, 0.92)', border: '1px solid rgba(251, 182, 223, 0.55)', color: '#fff7fb' },
    { background: 'rgba(99, 102, 241, 0.92)', border: '1px solid rgba(165, 180, 252, 0.55)', color: '#f5f7ff' },
  ] as const;
  const columnId = upc.itemNumber;
  const {
    barcodeRows,
    caseRows,
    unmatchedDesigns,
    barcodeEntryByNormalized,
  } = React.useMemo(() => buildDerivedBarcodeRows(upc), [upc]);
  const allUpcEntries = React.useMemo(() => getAllBarcodeEntries(upc), [upc]);

  const totalCrops = upc.designs.reduce((a, v) => a + v.crops.length, 0) + (upc.pendingCrops?.length || 0) + upc.unassignedCrops.length + upc.nonFrontCrops.length + upc.cartonCrops.length + (upc.inactiveCrops?.length || 0);
  const upcCount = barcodeRows.length + caseRows.length;
  const numberedUpcMetaByNormalized = new Map(
    barcodeRows
      .map((row, index) => [normalizeBarcode(row.upc), {
        number: index + 1,
        ...UPC_NUMBER_COLORS[index % UPC_NUMBER_COLORS.length],
      }] as const)
      .filter(([normalized]) => Boolean(normalized))
      .filter(([normalized], index, entries) => entries.findIndex(([value]) => value === normalized) === index)
  );
  const knownBarcodes = new Set(barcodeRows.map((row) => normalizeBarcode(row.upc)).filter(Boolean));

  const preloadGroup = (groupKey: string, crops: MasterCropData[]) => {
    if (!onPreloadGroup || preloadedGroups.current.has(groupKey)) return;
    preloadedGroups.current.add(groupKey);
    onPreloadGroup(crops.map(c => c.imageUrl));
  };

  // Stable sort — scan-sourced crops first, then ID order within each source bucket.
  const sorted = (crops: MasterCropData[]) => [...crops].sort((a, b) => {
    const aScan = a.source === 'scan' ? 0 : 1;
    const bScan = b.source === 'scan' ? 0 : 1;
    if (aScan !== bScan) return aScan - bScan;
    return a.id.localeCompare(b.id);
  });
  const cropNeedsBarcodeWarning = (crop: MasterCropData) => (
    crop.source === 'scan'
      && crop.classificationType !== 'carton'
      && knownBarcodes.size > 0
      && !knownBarcodes.has(normalizeBarcode(crop.barcode))
  );
  const getCropSourceBadgeLabel = (crop: MasterCropData): 'S' | 'F' | 'C' | 'O' => (
    crop.source === 'scan'
      ? 'S'
      : crop.source === 'forge'
        ? 'F'
        : crop.source === 'control'
          ? 'C'
          : 'O'
  );
  const getCropBadgeConfig = (
    crop: MasterCropData,
    sectionType: 'front' | 'non-front' | 'carton'
  ): CropBadgeConfig => {
    const isCarton = sectionType === 'carton' || crop.classificationType === 'carton';
    const baseConfig = {
      sourceLabel: getCropSourceBadgeLabel(crop),
      typeLabel: isCarton ? ('C' as const) : ('P' as const),
    };

    if (crop.source !== 'scan') {
      return {
        ...baseConfig,
        topLeftMode: null,
      } as const;
    }

    const matchedBadge = numberedUpcMetaByNormalized.get(normalizeBarcode(crop.barcode));
    if (matchedBadge) {
      return {
        ...baseConfig,
        topLeftMode: 'number' as const,
        topLeftLabel: String(matchedBadge.number),
        topLeftBackground: matchedBadge.background,
        topLeftBorder: matchedBadge.border,
        topLeftColor: matchedBadge.color,
      };
    }

    return {
      ...baseConfig,
      topLeftMode: cropNeedsBarcodeWarning(crop) ? ('warning' as const) : null,
    };
  };
  const buildCropZoomMeta = (crop: MasterCropData): ZoomMeta => ({
    id: crop.id,
    source: crop.source,
    barcode: crop.barcode,
    normalizedUpc: crop.normalizedUpc,
    classificationType: crop.classificationType,
    warningMessage: cropNeedsBarcodeWarning(crop) ? 'Barcode does not match known barcodes' : undefined,
    imageResolution: crop.imageResolution,
    createdBy: crop.createdBy,
    createdAt: crop.createdAt,
    updatedBy: crop.updatedBy,
    updatedAt: crop.updatedAt,
  });
  function getEntryImages(entry: MasterBarcodeEntry) {
    const seen = new Set<string>();
    return entry.storeDetails
      .flatMap((detail) => detail.images)
      .filter((image) => {
        const key = `${image.url}::${image.sourceLabel || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }
  function getEntrySharedDisplay(entry: MasterBarcodeEntry) {
    const details = entry.storeDetails;
    if (details.length === 0) return null;
    const signatures = details.map((detail) => JSON.stringify({
      name: detail.name || '',
      casePack: detail.casePack ?? null,
      images: detail.images.map((image) => image.url),
    }));
    if (signatures.every((signature) => signature === signatures[0])) {
      return details[0];
    }
    return null;
  }
  const getResolvedDesignBarcodeInfo = (design: typeof upc.designs[number]) => (
    getDesignBarcodeInfo(design, barcodeEntryByNormalized)
  );
  const toggleCollapsedSection = (sectionKey: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };
  const formatCoverageSummary = (designCount: number, cropCount: number) => {
    if (designCount === 0 && cropCount === 0) return null;
    return `${designCount} design${designCount === 1 ? '' : 's'} / ${cropCount} crop${cropCount === 1 ? '' : 's'}`;
  };
  const formatStoreLabel = (detail: typeof allUpcEntries[number]['storeDetails'][number]) => (
    `${detail.storeLabel}${detail.role === 'primary' ? ' (p)' : ''}`
  );
  const headerSurface = 'var(--elevated)';
  const cardSurface = 'var(--surface)';
  const subtleSurface = 'color-mix(in srgb, var(--elevated) 84%, transparent)';
  const softDivider = 'color-mix(in srgb, var(--border) 70%, transparent)';
  const renderUpcNumberBadge = (normalizedBarcode: string) => {
    const badge = numberedUpcMetaByNormalized.get(normalizedBarcode);
    if (!badge) return null;
    return (
      <div
        style={{
          width: 16,
          height: 16,
          padding: 0,
          borderRadius: 999,
          background: badge.background,
          border: badge.border,
          color: badge.color,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: scaleText(8),
          fontWeight: 700,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {badge.number}
      </div>
    );
  };
  const renderBarcodeImages = (
    images: { url: string; sourceLabel?: string }[],
    imageSize = upcEntryImageSize
  ) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {images.map((image) => (
        <div
          key={`${image.url}-${image.sourceLabel || 'image'}`}
          title={image.sourceLabel || 'image'}
          style={{
            width: imageSize,
            height: imageSize,
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid var(--border)',
            background: '#000',
            flexShrink: 0,
          }}
          onMouseEnter={onImageHover ? (e) => onImageHover(e, image.url, image.sourceLabel ? { source: image.sourceLabel } : undefined) : undefined}
          onMouseMove={onImageMove ? (e) => onImageMove(e, image.url, image.sourceLabel ? { source: image.sourceLabel } : undefined) : undefined}
          onMouseLeave={onImageLeave}
        >
          <img
            src={getCachedImageUrl(image.url)}
            alt={image.sourceLabel || 'barcode image'}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
            draggable={false}
          />
        </div>
      ))}
    </div>
  );
  // ── Render helpers ──

  const renderCrop = (crop: MasterCropData, sectionType: 'front' | 'non-front' | 'carton', designId?: string, isRepresentative = false, isPending = false) => {
    const isFocusedCrop = !!(focusedHighlight && (
      focusedHighlight.cropId === crop.id ||
      (focusedHighlight.cropIds && focusedHighlight.cropIds.includes(crop.id))
    ));
    const isLinkHighlighted = highlightedCropIds?.has(crop.id) ?? false;
    const badgeConfig = getCropBadgeConfig(crop, sectionType);
    return (
      <CropThumbnail
        key={crop.id}
        crop={crop}
        sectionType={sectionType}
        size={resolvedImageSize}
        isSelected={selectedCropIds.has(crop.id)}
        isRepresentative={isRepresentative}
        isFocused={isFocusedCrop}
        isLinkHighlighted={isLinkHighlighted}
        highlightColor={highlightColor}
        highlightGlow={highlightGlow}
        isPending={isPending}
        showTriangle={showTriangles}
        isRecentlyMoved={recentlyMovedCropIds?.has(crop.id) ?? false}
        reloadVersion={reloadVersion}
        topLeftBadgeMode={badgeConfig.topLeftMode}
        topLeftBadgeLabel={badgeConfig.topLeftLabel}
        topLeftBadgeBackground={badgeConfig.topLeftBackground}
        topLeftBadgeBorder={badgeConfig.topLeftBorder}
        topLeftBadgeColor={badgeConfig.topLeftColor}
        sourceBadgeLabel={badgeConfig.sourceLabel}
        typeBadgeLabel={badgeConfig.typeLabel}
        onClick={(e) => { e.stopPropagation(); onCropClick(crop.id, e.shiftKey); }}
        onImageHover={onImageHover}
        onImageMove={onImageMove}
        onImageLeave={onImageLeave}
      />
    );
  };

  const renderSectionHeader = (
    label: string,
    count: number,
    sectionKey: string,
    target: SectionTarget,
    options?: { color?: string; isMergeSource?: boolean; barcodeStatus?: BarcodeStatus }
  ) => {
    const isCollapsed = collapsedSections.has(sectionKey);
    const isExplicitDesignTarget = target.type !== 'design' || !!target.designId;
    const isDropTarget =
      (mode === 'crops-selected' || (mode === 'merge-source' && target.type === 'design' && target.designId !== mergeSourceDesignId)) &&
      target.type !== 'pending' &&
      isExplicitDesignTarget;
    const isClickable = isDropTarget || options?.isMergeSource || mode === 'idle';
    const isMergeSource = options?.isMergeSource;

    // Layout: [collapse 22px fixed] [label ... flex] [count]
    // The 22px column ensures labels always start at the same X regardless of collapse icon
    return (
      <div
        onClick={() => {
          if (isDropTarget || isMergeSource || mode === 'idle') {
            onSectionClick(target);
          }
        }}
        style={{
          display: 'flex', alignItems: 'center', padding: '6px 10px 6px 2px',
          cursor: isClickable ? 'pointer' : 'default',
          borderRadius: 'var(--radius-sm)',
          borderLeft: isMergeSource ? '3px solid var(--danger)' : '3px solid transparent',
          border: `1px solid ${isMergeSource ? 'var(--danger)' : softDivider}`,
          background: isMergeSource ? 'var(--danger-subtle)' : headerSurface,
          boxShadow: 'var(--shadow-card)',
          transition: 'all 150ms ease',
          userSelect: 'none',
        }}
        onMouseEnter={e => {
          if (isDropTarget) { e.currentTarget.style.background = mode === 'merge-source' ? 'var(--positive-subtle)' : 'var(--purple-subtle)'; e.currentTarget.style.borderLeftColor = mode === 'merge-source' ? 'var(--positive)' : 'var(--purple)'; }
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = isMergeSource ? 'var(--danger-subtle)' : 'transparent';
          e.currentTarget.style.borderLeftColor = isMergeSource ? 'var(--danger)' : 'transparent';
        }}
      >
        <div style={{ width: HEADER_SIDE_WIDTH, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {count > 0 ? (
            <div onClick={(e) => { e.stopPropagation(); setCollapsedSections(prev => { const n = new Set(prev); if (n.has(sectionKey)) n.delete(sectionKey); else n.add(sectionKey); return n; }); }} style={{ cursor: 'pointer', display: 'flex' }}>
              {isCollapsed
                ? <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                : <KeyboardArrowDownIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
              }
            </div>
          ) : (
            <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'transparent' }} />
          )}
        </div>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: scaleText(9), fontWeight: 700,
          color: isMergeSource ? 'var(--danger)' : 'var(--text-tertiary)',
          textTransform: 'uppercase', letterSpacing: '1px',
          flex: 1, textAlign: 'center',
        }}>{label}</span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: scaleText(9),
          color: 'var(--text-tertiary)',
          flexShrink: 0,
          width: HEADER_COUNT_WIDTH,
          textAlign: 'right',
        }}>{count}</span>
        {isMergeSource && (
          <span style={{ fontSize: scaleText(8), fontWeight: 700, color: 'var(--danger)', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>
            SOURCE
          </span>
        )}
      </div>
    );
  };

  const checkHighlight = (sectionKey: string, target: SectionTarget, count?: number) => {
    return isSectionHighlighted ? isSectionHighlighted(sectionKey, target, count) : false;
  };

  const renderStaticHeader = (label: string, count: number, sectionKey: string) => {
    const isCollapsed = collapsedSections.has(sectionKey);

    return (
      <div
        onClick={() => toggleCollapsedSection(sectionKey)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px 6px 2px',
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: 'var(--radius-sm)',
          border: `1px solid ${softDivider}`,
          background: headerSurface,
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <div style={{ width: HEADER_SIDE_WIDTH, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isCollapsed
            ? <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
            : <KeyboardArrowDownIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
          }
        </div>
        <div style={{
          flex: 1,
          minWidth: 0,
          fontFamily: 'var(--font-mono)',
          fontSize: scaleText(9),
          fontWeight: 700,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          color: 'var(--text-tertiary)',
          textAlign: 'center',
        }}>
          {label}
        </div>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: scaleText(10),
          color: 'var(--text-tertiary)',
          width: HEADER_COUNT_WIDTH,
          textAlign: 'right',
          flexShrink: 0,
        }}>
          {count}
        </span>
      </div>
    );
  };

  const renderDesignSection = (design: typeof upc.designs[number], groupKeyPrefix: string) => {
    const sectionKey = `design-${design.id}`;
    const isMergeSource = mergeSourceDesignId === design.id;
    const designTarget: SectionTarget = { itemNumber: columnId, type: 'design', designId: design.id };
    const isDesignHighlighted = checkHighlight(sectionKey, designTarget);

    const isConfirmed = design.barcodeStatus === 'confirmed';
    const isBarcPending = design.barcodeStatus === 'pending';
    const designBarcodeInfo = getResolvedDesignBarcodeInfo(design);
    const showReliableDesignLabel = Boolean(designBarcodeInfo.normalizedBarcode);
    const repCrop = design.crops.find((crop) => crop.id === design.representativeCropId);
    const visibleDesignCrops = design.crops;
    const showCropStrip = visibleDesignCrops.length > 0;
    const refImgSize = resolvedImageSize;
    const badgeSize = Math.max(11, Math.round(resolvedImageSize * 0.32));
    const iconSize = Math.max(14, Math.round(resolvedImageSize * 0.4));
    const isCollapsed = collapsedSections.has(sectionKey);
    const isDropTarget = (mode === 'crops-selected' || (mode === 'merge-source' && designTarget.designId !== mergeSourceDesignId)) && designTarget.type !== 'pending';
    const isClickable = isDropTarget || isMergeSource || mode === 'idle';

    const refBoxStyle: React.CSSProperties = {
      width: refImgSize,
      height: refImgSize,
      flexShrink: 0,
      position: 'relative',
      overflow: 'visible',
      borderRadius: 3,
    };
    const auxThumbGap = 4;
    const auxThumbSize = Math.max(28, Math.floor((refImgSize - auxThumbGap) / 2));
    const renderAuxMediaSlot = (
      key: string,
      options: {
        imageUrl?: string;
        alt: string;
        title: string;
        label?: string;
        icon?: React.ReactNode;
        meta?: ZoomMeta;
      }
    ) => {
      const imageUrl = options.imageUrl;

      return (
        <div
        key={key}
        title={options.title}
        style={{
          width: auxThumbSize,
          height: auxThumbSize,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${softDivider}`,
          background: imageUrl ? '#000' : 'var(--elevated)',
          color: 'var(--text-tertiary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        onMouseEnter={imageUrl && onImageHover ? (e) => onImageHover(e, imageUrl, options.meta) : undefined}
        onMouseMove={imageUrl && onImageMove ? (e) => onImageMove(e, imageUrl, options.meta) : undefined}
        onMouseLeave={imageUrl ? onImageLeave : undefined}
      >
        {imageUrl ? (
          <img
            src={getCachedImageUrl(imageUrl)}
            alt={options.alt}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
            draggable={false}
          />
        ) : options.icon ? (
          options.icon
        ) : (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: scaleText(8),
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            lineHeight: 1,
          }}>
            {options.label}
          </span>
        )}
      </div>
      );
    };

    return (
      <div
        key={`${groupKeyPrefix}-${design.id}`}
        style={{
          padding: '4px',
          marginBottom: 4,
          borderRadius: 'var(--radius-sm)',
          background: isDesignHighlighted ? 'var(--purple-subtle)' : subtleSurface,
          boxShadow: isDesignHighlighted
            ? 'inset 0 0 0 1px var(--purple-border), var(--shadow-card)'
            : `inset 0 0 0 1px ${softDivider}, var(--shadow-card)`,
          transition: 'all 150ms ease',
        }}
        onClick={() => { if (mode === 'idle') onActiveDesignChange(activeDesignId === design.id ? null : design.id); }}
      >
        <div
          onClick={(e) => {
            if (isDropTarget || isMergeSource || mode === 'idle') {
              e.stopPropagation();
              onSectionClick(designTarget);
            }
          }}
          onMouseEnter={(e) => {
            if (isDropTarget) {
              e.currentTarget.style.background = mode === 'merge-source' ? 'var(--positive-subtle)' : 'var(--purple-subtle)';
              e.currentTarget.style.borderLeftColor = mode === 'merge-source' ? 'var(--positive)' : 'var(--purple)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isMergeSource ? 'var(--danger-subtle)' : 'transparent';
            e.currentTarget.style.borderLeftColor = isMergeSource ? 'var(--danger)' : 'transparent';
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px 6px 2px',
            cursor: isClickable ? 'pointer' : 'default',
            borderRadius: 'var(--radius-sm)',
            borderLeft: isMergeSource ? '3px solid var(--danger)' : '3px solid transparent',
            border: `1px solid ${isMergeSource ? 'var(--danger)' : softDivider}`,
            background: isMergeSource ? 'var(--danger-subtle)' : cardSurface,
            transition: 'all 150ms ease',
            userSelect: 'none',
          }}
        >
          <div style={{ width: 22, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {design.crops.length > 0 && (
              <div onClick={(e) => { e.stopPropagation(); toggleCollapsedSection(sectionKey); }} style={{ cursor: 'pointer', display: 'flex' }}>
                {isCollapsed
                  ? <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                  : <KeyboardArrowDownIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                }
              </div>
            )}
          </div>
          {isConfirmed && design.frontImageUrl ? (
            <div style={{ ...refBoxStyle }}>
              <div
                style={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: 3, border: '1px solid var(--border)' }}
                onMouseEnter={onImageHover ? (e) => onImageHover(e, design.frontImageUrl!) : undefined}
                onMouseMove={onImageMove ? (e) => onImageMove(e, design.frontImageUrl!) : undefined}
                onMouseLeave={onImageLeave}
              >
                <img src={getCachedImageUrl(design.frontImageUrl)} alt="Front" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', display: 'block' }} draggable={false} />
              </div>
              <CheckCircleIcon style={{ position: 'absolute', top: -4, right: -4, fontSize: badgeSize, color: '#E8976C', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} />
            </div>
          ) : (
            <div style={{ ...refBoxStyle }}>
              <div style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                borderRadius: 3,
                border: `1px dashed ${isBarcPending ? 'var(--warning)' : 'var(--border-hover)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--surface)',
              }}>
                {isBarcPending
                  ? <HourglassEmptyIcon style={{ fontSize: iconSize, color: 'var(--warning)', opacity: 0.5 }} />
                  : <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(10), fontWeight: 700, letterSpacing: '0.04em', color: 'var(--text-tertiary)', opacity: 0.45, lineHeight: 1 }}>F</span>
                }
              </div>
              <CheckCircleIcon style={{ position: 'absolute', top: -4, right: -4, fontSize: badgeSize, color: 'var(--text-tertiary)', opacity: 0.4, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} />
            </div>
          )}
          {repCrop ? (() => {
            const repMeta = buildCropZoomMeta(repCrop);
            const isRepSelected = selectedCropIds.has(repCrop.id);
            const isRepHighlighted = highlightedCropIds?.has(repCrop.id) ?? false;
            return <div style={{ ...refBoxStyle }}>
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  borderRadius: 3,
                  cursor: 'default',
                  border: isRepSelected
                    ? '2px solid var(--blue)'
                    : isRepHighlighted
                      ? `3px solid ${highlightColor || 'var(--purple)'}`
                      : '1px solid var(--border)',
                  boxShadow: isRepHighlighted ? (highlightGlow || '0 0 12px rgba(188,140,255,0.6), 0 0 24px rgba(188,140,255,0.25)') : 'none',
                }}
                onMouseEnter={onImageHover ? (e) => onImageHover(e, repCrop.imageUrl, repMeta) : undefined}
                onMouseMove={onImageMove ? (e) => onImageMove(e, repCrop.imageUrl, repMeta) : undefined}
                onMouseLeave={onImageLeave}
              >
                <img src={getCachedImageUrl(repCrop.imageUrl)} alt="Rep" style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', display: 'block' }} draggable={false} />
              </div>
              <StarIcon style={{ position: 'absolute', top: -4, right: -4, fontSize: badgeSize, color: '#E8976C', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} />
            </div>;
          })() : (
            <div style={{ ...refBoxStyle, overflow: 'hidden', border: '1px dashed var(--border-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)' }}>
              <StarIcon style={{ fontSize: iconSize, color: 'var(--text-tertiary)', opacity: 0.2 }} />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: auxThumbGap, flexShrink: 0 }}>
            {renderAuxMediaSlot(`back-${design.id}`, {
              imageUrl: isConfirmed ? design.backImageUrl : undefined,
              alt: 'Back',
              label: 'B',
              title: isConfirmed && design.backImageUrl ? 'Back image' : 'No back image',
              meta: isConfirmed && design.backImageUrl ? { source: 'design back' } : undefined,
            })}
            {renderAuxMediaSlot(`barcode-${design.id}`, {
              imageUrl: isConfirmed ? design.barcodeImageUrl : undefined,
              alt: 'Barcode',
              icon: <QrCode2OutlinedIcon style={{ fontSize: 12, color: 'var(--text-tertiary)' }} />,
              title: isConfirmed && design.barcodeImageUrl ? 'Barcode image' : 'No barcode image',
              meta: isConfirmed && design.barcodeImageUrl ? { source: 'design barcode' } : undefined,
            })}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(11), fontWeight: 600, color: isMergeSource ? 'var(--danger)' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {showReliableDesignLabel ? (designBarcodeInfo.productLabel || designBarcodeInfo.barcode || 'Unknown') : 'Unknown'}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(9), color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {showReliableDesignLabel ? (
                <>
                  {designBarcodeInfo.barcode ? `${designBarcodeInfo.barcode}  ` : ''}
                  {design.id}
                </>
              ) : (
                design.id
              )}
            </span>
          </div>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(10), color: 'var(--text-tertiary)', flexShrink: 0 }}>
            {design.crops.length}
          </span>
        </div>
        {!isCollapsed && showCropStrip && (
          <div
            onMouseEnter={() => preloadGroup(`var-${design.id}`, design.crops)}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              padding: '4px 8px 2px 30px',
              alignItems: 'center',
              marginTop: 2,
            }}
          >
            {sorted(visibleDesignCrops).map((crop) => renderCrop(crop, 'front', design.id, crop.id === design.representativeCropId))}
          </div>
        )}
      </div>
    );
  };

  // ── Main render ──

  return (
    <div style={{
      flex: 1, minWidth: 200,
      border: '1px solid var(--border)', borderRadius: 'var(--radius)',
      background: 'var(--surface)', display: 'flex', flexDirection: 'column',
      maxHeight: '100%', overflow: 'hidden',
    }}>
      {/* UPC header */}
      <div style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
        {/* Product images — left side */}
        {upc.alternativeImageUrls && upc.alternativeImageUrls.length > 0 && (
          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
            {upc.alternativeImageUrls.map((url, i) => (
              <div
                key={i}
                style={{ position: 'relative', overflow: 'hidden', borderRadius: 3, border: '1px solid var(--border)', flexShrink: 0 }}
                onMouseEnter={onImageHover ? (e) => onImageHover(e, url) : undefined}
                onMouseMove={onImageMove ? (e) => onImageMove(e, url) : undefined}
                onMouseLeave={onImageLeave}
              >
                <img src={getCachedImageUrl(url)} alt={`Alt ${i + 1}`} style={{
                  width: resolvedImageSize,
                  height: resolvedImageSize,
                  objectFit: 'contain',
                  background: '#000',
                  display: 'block',
                }} draggable={false} />
              </div>
            ))}
          </div>
        )}
        {/* Text info — right side */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(11), fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {upc.productName}
              </div>
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(9), color: 'var(--text-tertiary)', flexShrink: 0 }}>
              {totalCrops}
            </span>
            {onRemoveUpc && (
              <button
                onClick={() => onRemoveUpc(columnId)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                  color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', flexShrink: 0,
                }}
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, fontFamily: 'var(--font-mono)', fontSize: scaleText(9), color: 'var(--text-tertiary)' }}>
            {upc.itemNumber && (
              <span>ITEM_NUMBER <span style={{ color: 'var(--text-secondary)' }}>{upc.itemNumber}</span></span>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable sections — fixed order: Pending, Unassigned, Designs, Carton, Non-front */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* ── Group: Barcodes & Designs (blue-grey) ── */}
        {(allUpcEntries.length > 0 || unmatchedDesigns.length > 0) && (
          <div style={{
            borderLeft: '2px solid var(--accent-border)', marginLeft: 1,
            background: 'var(--accent-subtle)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
            paddingTop: 4, paddingBottom: 4,
          }}>
            <div style={{ padding: '0 4px' }}>
              {renderStaticHeader('BARCODES & DESIGNS', upcCount + unmatchedDesigns.length, `barcodes-${columnId}`)}
              {!collapsedSections.has(`barcodes-${columnId}`) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '2px 8px 8px 0' }}>
                  {barcodeRows.map((row) => {
                    const rowSectionKey = `barcode-row-${columnId}-${row.normalizedBarcode}`;
                    const isRowCollapsed = collapsedSections.has(rowSectionKey);
                    const rowTarget: SectionTarget = { itemNumber: columnId, type: 'barcode', barcode: row.upc };
                    const rowIsDirectMoveTarget = mode === 'crops-selected' && row.designCount <= 1;
                    const sharedDisplay = getEntrySharedDisplay(row.entry);
                    const entryImages = getEntryImages(row.entry);
                    const entryNumberBadge = renderUpcNumberBadge(row.normalizedBarcode);

                    return (
                      <div
                        key={row.key}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6,
                          padding: '8px 10px 10px 6px',
                          borderRadius: 'calc(var(--radius-sm) + 1px)',
                          background: subtleSurface,
                          boxShadow: `inset 0 0 0 1px ${softDivider}, var(--shadow-card)`,
                        }}
                      >
                        <div
                          onClick={() => {
                            if (mode === 'idle') {
                              toggleCollapsedSection(rowSectionKey);
                              return;
                            }
                            if (rowIsDirectMoveTarget) {
                              onSectionClick(rowTarget);
                              return;
                            }
                            toggleCollapsedSection(rowSectionKey);
                          }}
                          onMouseEnter={(e) => {
                            if (rowIsDirectMoveTarget) {
                              e.currentTarget.style.background = 'var(--purple-subtle)';
                              e.currentTarget.style.borderLeftColor = 'var(--purple)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderLeftColor = 'transparent';
                          }}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: `${HEADER_SIDE_WIDTH}px ${upcBadgeColumnWidth}px ${upcCodeColumnWidth} minmax(0, 1fr) auto`,
                            columnGap: upcEntryColumnGap,
                            alignItems: 'center',
                            padding: '6px 8px 6px 2px',
                            borderRadius: 'var(--radius-sm)',
                            borderLeft: '3px solid transparent',
                            border: `1px solid ${softDivider}`,
                            background: cardSurface,
                            cursor: mode === 'idle' || rowIsDirectMoveTarget ? 'pointer' : 'default',
                            transition: 'all 150ms ease',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div onClick={(e) => { e.stopPropagation(); toggleCollapsedSection(rowSectionKey); }} style={{ cursor: 'pointer', display: 'flex' }}>
                              {isRowCollapsed
                                ? <KeyboardArrowRightIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                                : <KeyboardArrowDownIcon style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                              }
                            </div>
                          </div>
                          <div style={{ minHeight: upcEntryImageSize, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            {entryNumberBadge}
                          </div>
                          <div style={{ minHeight: upcEntryImageSize, display: 'flex', alignItems: 'center', fontFamily: 'var(--font-mono)', fontSize: scaleText(9), color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                            {row.entry.upc}
                          </div>
                          <div style={{ minWidth: 0, minHeight: upcEntryImageSize, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3 }}>
                            {sharedDisplay ? (
                              <>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(9), color: 'var(--text-tertiary)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {row.entry.storeDetails.map((detail) => formatStoreLabel(detail)).join('/')}
                                </span>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(9), color: 'var(--text-secondary)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {sharedDisplay.name || ''}
                                  {sharedDisplay.casePack ? ` (${sharedDisplay.casePack})` : ''}
                                </span>
                              </>
                            ) : (
                              row.entry.storeDetails.map((detail) => (
                                <div key={`${row.entry.upc}-${detail.storeLabel}-${detail.role}`} style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: scaleText(9), lineHeight: 1.4 }}>
                                  <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {formatStoreLabel(detail)}
                                  </span>
                                  <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {detail.name || ''}
                                    {detail.casePack ? ` (${detail.casePack})` : ''}
                                  </span>
                                </div>
                              ))
                            )}
                            {formatCoverageSummary(row.designCount, row.cropCount) && (
                              <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(8), color: row.designCount === 0 ? 'var(--warning)' : 'var(--text-tertiary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                {formatCoverageSummary(row.designCount, row.cropCount)}
                              </span>
                            )}
                          </div>
                          {entryImages.length > 0 ? (
                            <div
                              style={{
                                width: Math.min(entryImages.length, 2) * upcEntryImageSize
                                  + Math.max(Math.min(entryImages.length, 2) - 1, 0) * 4,
                                justifySelf: 'end',
                              }}
                            >
                              {renderBarcodeImages(entryImages, upcEntryImageSize)}
                            </div>
                          ) : null}
                        </div>

                        {!isRowCollapsed && row.matchedDesigns.length > 0 && (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            marginLeft: 4,
                            paddingLeft: 3,
                            borderLeft: '2px solid var(--accent-border)',
                          }}>
                            {row.matchedDesigns.map((design) => renderDesignSection(design, `barcode-${row.normalizedBarcode}`))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {caseRows.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {renderStaticHeader('CASE', caseRows.length, `cases-${columnId}`)}
                      {!collapsedSections.has(`cases-${columnId}`) && caseRows.map((row) => {
                        const sharedDisplay = getEntrySharedDisplay(row.entry);
                        const entryImages = getEntryImages(row.entry);
                        return (
                        <div
                          key={row.key}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: `${HEADER_SIDE_WIDTH}px ${upcCodeColumnWidth} minmax(0, 1fr) auto`,
                            columnGap: upcEntryColumnGap,
                            alignItems: 'center',
                            padding: '8px 10px 10px 6px',
                            borderRadius: 'calc(var(--radius-sm) + 1px)',
                            background: subtleSurface,
                            boxShadow: `inset 0 0 0 1px ${softDivider}`,
                          }}
                        >
                            <div />
                            <div style={{ minHeight: upcEntryImageSize, display: 'flex', alignItems: 'center', fontFamily: 'var(--font-mono)', fontSize: scaleText(9), color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                              {row.entry.upc}
                            </div>
                            <div style={{ minWidth: 0, minHeight: upcEntryImageSize, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
                              {sharedDisplay ? (
                                <>
                                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(9), color: 'var(--text-tertiary)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.entry.storeDetails.map((detail) => detail.storeLabel).join('/')}
                                  </span>
                                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: scaleText(9), color: 'var(--text-secondary)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {sharedDisplay.name || ''}
                                    {sharedDisplay.casePack ? ` (${sharedDisplay.casePack})` : ''}
                                  </span>
                                </>
                              ) : (
                                row.entry.storeDetails.map((detail) => (
                                  <div key={`${row.entry.upc}-${detail.storeLabel}`} style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: scaleText(9), lineHeight: 1.4 }}>
                                    <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                      {detail.storeLabel}
                                    </span>
                                    <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {detail.name || ''}
                                      {detail.casePack ? ` (${detail.casePack})` : ''}
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                            {entryImages.length > 0 ? (
                              <div
                                style={{
                                  width: Math.min(entryImages.length, 2) * upcEntryImageSize
                                    + Math.max(Math.min(entryImages.length, 2) - 1, 0) * 4,
                                  justifySelf: 'end',
                                }}
                              >
                                {renderBarcodeImages(entryImages, upcEntryImageSize)}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {unmatchedDesigns.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {renderStaticHeader('UNMATCHED DESIGNS', unmatchedDesigns.length, `unmatched-${columnId}`)}
                      {!collapsedSections.has(`unmatched-${columnId}`) && unmatchedDesigns.map((design) => (
                        renderDesignSection(design, 'unmatched')
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Group: Pending (purple) ── */}
        <div style={{
          borderLeft: '2px solid rgba(163,113,247,0.35)', marginLeft: 1,
          background: 'rgba(163,113,247,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {(() => {
          const pending = upc.pendingCrops || [];
          const pendingSectionKey = `pending-${columnId}`;
          const pendingTarget: SectionTarget = { itemNumber: columnId, type: 'pending' };
          const isPendingHighlighted = checkHighlight(pendingSectionKey, pendingTarget, pending.length);
          return (
            <div style={{
              padding: '0 4px', marginBottom: 2,
              borderRadius: 'var(--radius-sm)',
              background: isPendingHighlighted ? 'var(--purple-subtle)' : 'transparent',
              boxShadow: isPendingHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
              transition: 'all 150ms ease',
            }}>
              {renderSectionHeader('Pending', pending.length, pendingSectionKey, pendingTarget)}
              {pending.length > 0 && !collapsedSections.has(pendingSectionKey) && (
                <div onMouseEnter={() => preloadGroup('pending-' + columnId, pending)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 8px 4px 0' }}>
                  {sorted(pending).map(crop => renderCrop(crop, 'front', undefined, false, true))}
                </div>
              )}
            </div>
          );
        })()}
        </div>

        {/* ── Group: Unassigned (grey) ── */}
        <div style={{
          borderLeft: '2px solid rgba(139,148,158,0.35)', marginLeft: 1,
          background: 'rgba(139,148,158,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {(() => {
          const unassignedSectionKey = `novar-${columnId}`;
          const unassignedTarget: SectionTarget = { itemNumber: columnId, type: 'no-design' };
          const isUnassignedHighlighted = checkHighlight(unassignedSectionKey, unassignedTarget, upc.unassignedCrops.length);
          return (
            <div style={{
              padding: '0 4px', marginBottom: 2,
              borderRadius: 'var(--radius-sm)',
              background: isUnassignedHighlighted ? 'var(--purple-subtle)' : 'transparent',
              boxShadow: isUnassignedHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
              transition: 'all 150ms ease',
            }}>
              {renderSectionHeader('Unassigned', upc.unassignedCrops.length, unassignedSectionKey, unassignedTarget)}
              {upc.unassignedCrops.length > 0 && !collapsedSections.has(unassignedSectionKey) && (
                <div onMouseEnter={() => preloadGroup('unassigned-' + columnId, upc.unassignedCrops)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 8px 4px 0' }}>
                  {sorted(upc.unassignedCrops).map(crop => renderCrop(crop, 'front'))}
                </div>
              )}
            </div>
          );
        })()}
        </div>

        {/* ── Group: Cartons (orange) ── */}
        <div style={{
          borderLeft: '2px solid rgba(232,151,108,0.35)', marginLeft: 1,
          background: 'rgba(232,151,108,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {(() => {
          const cartonSectionKey = `carton-${columnId}`;
          const cartonTarget: SectionTarget = { itemNumber: columnId, type: 'carton' };
          const isCartonHighlighted = checkHighlight(cartonSectionKey, cartonTarget);
          return (
            <div style={{
              padding: '0 4px', marginBottom: 2,
              borderRadius: 'var(--radius-sm)',
              background: isCartonHighlighted ? 'var(--purple-subtle)' : 'transparent',
              boxShadow: isCartonHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
              transition: 'all 150ms ease',
            }}>
              {renderSectionHeader('Cartons', upc.cartonCrops.length, cartonSectionKey, cartonTarget)}
              {!collapsedSections.has(cartonSectionKey) && (
                <div onMouseEnter={() => preloadGroup('carton-' + columnId, upc.cartonCrops)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 8px 4px 0' }}>
                  {sorted(upc.cartonCrops).map(crop => renderCrop(crop, 'carton'))}
                </div>
              )}
            </div>
          );
        })()}
        </div>

        {/* ── Group: Non-front (yellow) ── */}
        <div style={{
          borderLeft: '2px solid rgba(227,179,65,0.35)', marginLeft: 1,
          background: 'rgba(227,179,65,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {/* Non-front */}
        {(() => {
          const backImages = upc.designs
            .filter(v => v.backImageUrl)
            .map(v => ({ designId: v.id, url: v.backImageUrl! }));
          const totalNonFront = upc.nonFrontCrops.length + backImages.length;
          const nfSectionKey = `nf-${columnId}`;
          const nfTarget: SectionTarget = { itemNumber: columnId, type: 'non-front' };
          const isNfHighlighted = checkHighlight(nfSectionKey, nfTarget);
          return (
            <div style={{
              padding: '0 4px', marginBottom: 2,
              borderRadius: 'var(--radius-sm)',
              background: isNfHighlighted ? 'var(--purple-subtle)' : 'transparent',
              boxShadow: isNfHighlighted ? 'inset 0 0 0 1px var(--purple-border)' : 'none',
              transition: 'all 150ms ease',
            }}>
              {renderSectionHeader('Non-front', totalNonFront, nfSectionKey, nfTarget)}
              {totalNonFront > 0 && !collapsedSections.has(nfSectionKey) && (
                <div style={{ padding: '2px 8px 4px 0' }}>
                  {backImages.length > 0 && (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      padding: 3, marginBottom: 4,
                      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                      background: 'var(--elevated)',
                    }}>
                      {backImages.map(bi => (
                        <div
                          key={`back-${bi.designId}`}
                          style={{ position: 'relative', overflow: 'hidden', borderRadius: 3, border: '1px solid var(--border)', flexShrink: 0 }}
                          onMouseEnter={onImageHover ? (e) => onImageHover(e, bi.url) : undefined}
                          onMouseMove={onImageMove ? (e) => onImageMove(e, bi.url) : undefined}
                          onMouseLeave={onImageLeave}
                        >
                          <img src={getCachedImageUrl(bi.url)} alt="Back" style={{
                            width: resolvedImageSize,
                            height: resolvedImageSize,
                            objectFit: 'contain',
                            background: '#000',
                            display: 'block',
                          }} draggable={false} />
                          {showTriangles && (
                            <div style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, borderTop: '10px solid var(--warning)', borderRight: '10px solid transparent', pointerEvents: 'none' }} />
                          )}
                          <span style={{
                            position: 'absolute', bottom: 1, left: 1, fontSize: scaleText(7), fontWeight: 700,
                            fontFamily: 'var(--font-mono)', color: '#fff',
                            background: 'rgba(0,0,0,0.6)', padding: '0 3px', borderRadius: 2,
                            lineHeight: 1.3,
                          }}>B</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {upc.nonFrontCrops.length > 0 && (
                    <div onMouseEnter={() => preloadGroup('nf-' + columnId, upc.nonFrontCrops)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {sorted(upc.nonFrontCrops).map(crop => renderCrop(crop, 'non-front'))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
        </div>

        {/* ── Group: Inactive (red) ── */}
        <div style={{
          borderLeft: '2px solid rgba(248,81,73,0.35)', marginLeft: 1,
          background: 'rgba(248,81,73,0.04)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          paddingTop: 2, paddingBottom: 2,
        }}>
        {(() => {
          const inactiveCrops = upc.inactiveCrops || [];
          const inactiveSectionKey = `inactive-${columnId}`;
          return (
            <div style={{ padding: '0 4px', marginBottom: 2 }}>
              {renderSectionHeader('Inactive', inactiveCrops.length, inactiveSectionKey, { itemNumber: columnId, type: 'inactive' })}
              {inactiveCrops.length > 0 && !collapsedSections.has(inactiveSectionKey) && (
                <div onMouseEnter={() => preloadGroup('inactive-' + columnId, inactiveCrops)} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 8px 4px 0' }}>
                  {sorted(inactiveCrops).map(crop => renderCrop(crop, 'front'))}
                </div>
              )}
            </div>
          );
        })()}
        </div>
      </div>
    </div>
  );
};
